import Aos from "aos";
import { useEffect, useState, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { routes } from "../Routes/Routes";

import Like from "../Pages/Like/Like";
import User from "../Pages/User/User";
import Home from "../Pages/HomePage/Home";
import Summery from "../Pages/Summery/Summery";
import AddCart from "../Pages/AddCart/AddCart";
import Header from "../Component/Header/Header";
import Footer from "../Component/Footer/Footer";
import Signup from "../Pages/User/Signup/Signup";
import Shopping from "../Pages/Shopping/Shopping";
import ThankYou from "../Pages/ThankYou/ThankYou";
import Catagory from "../Pages/Catagory/Catagory";
import OrderPage from "../Pages/OrderPage/OrderPage";
import OrderView from "../Pages/OrderPage/OrderView/OrderView";
import SettingPage from "../Pages/User/SettingPage/SettingPage";
import Address from "../Pages/AddAddress/Address";
import SearchData from "../Pages/SearchData/SearchData";
import Faq from "../Pages/User/SettingPage/Faq";

import About from "../Pages/User/SettingPage/About";
import Craftsmanship from "../Pages/User/SettingPage/Craftsmanship";
import Refund from "../Pages/User/SettingPage/Refund";
import Privacy from "../Pages/User/SettingPage/Privacy";
import Contact from "../Pages/User/SettingPage/Contact/Contact";
import RetailerQuery from "../Pages/User/SettingPage/Contact/RetailerQuery";
import ThankYouMobile from "../Pages/ThankYou/ThankYouMobile";
import PaymentPending from "../Pages/ThankYou/PaymentPending";
import PaymentError from "../Pages/ThankYou/PaymentError";
import Error from "../Pages/ThankYou/error";
import LoadingScreen from "../Component/Loader";
import GetAQuote from "../Pages/SellGold/GetAQuote";
import DesignCAD from "../Pages/DesignCAD/DesignCAD";
import AuthGate from "../Component/AuthGate/AuthGate";
import ForgotPassword from "../Pages/User/ForgotPassword";
import ResetPassword from "../Pages/User/ResetPassword";
import AccountPortal from "../Pages/Account/AccountPortal";
import SeoMetaManager from "../Component/SeoMetaManager/SeoMetaManager";
import usePresence from "../utils/usePresence";

// Admin & factory modules — lazy-loaded so the customer-facing bundle
// doesn't carry their ~80 pages of code on first paint.
const AdminRouter = lazy(() => import(/* webpackChunkName: "admin" */ "./AdminRouter"));
const FactoryRouter = lazy(() => import(/* webpackChunkName: "factory" */ "./FactoryRouter"));

// Tennis Necklace concept studio — chooser + five competing feature-page
// concepts. Each demo lazy-loaded so its bundle (esp. the three.js one)
// only ships when the route is hit.
const TennisNecklaces = lazy(() => import(/* webpackChunkName: "tn-chooser" */ "../Pages/TennisNecklaces/TennisNecklaces"));
const Demo1Drape = lazy(() => import(/* webpackChunkName: "tn-drape" */ "../Pages/TennisNecklaces/Demo1Drape/Demo1Drape"));
const Demo2Atelier = lazy(() => import(/* webpackChunkName: "tn-atelier" */ "../Pages/TennisNecklaces/Demo2Atelier/Demo2Atelier"));
const Demo3LightTable = lazy(() => import(/* webpackChunkName: "tn-light" */ "../Pages/TennisNecklaces/Demo3LightTable/Demo3LightTable"));
const Demo4Configurator = lazy(() => import(/* webpackChunkName: "tn-os" */ "../Pages/TennisNecklaces/Demo4Configurator/Demo4Configurator"));
const Demo5Genome = lazy(() => import(/* webpackChunkName: "tn-genome" */ "../Pages/TennisNecklaces/Demo5Genome/Demo5Genome"));
const Demo6Builder = lazy(() => import(/* webpackChunkName: "tn-builder" */ "../Pages/TennisNecklaces/Demo6Builder/Demo6Builder"));
const Demo7Wizard = lazy(() => import(/* webpackChunkName: "tn-wizard" */ "../Pages/TennisNecklaces/Demo7Wizard/Demo7Wizard"));

const Layout = ({ children }) => {
  const location = useLocation();
  const isThankYouMobile = location.pathname === routes.thankyouMobileUrl;
  const isAdmin = location.pathname.startsWith("/admin");
  const isFactory = location.pathname.startsWith("/factory");
  const isTennisStudio = location.pathname.startsWith("/tennis-necklaces");
  // /t/<ref> is the jewellery-tag short link — it redirects straight into the
  // factory, so don't paint the storefront header behind it on the way past.
  const isTagLink = location.pathname.startsWith("/t/");

  usePresence();

  if (isAdmin || isFactory || isTennisStudio || isTagLink) return <>{children}</>;

  return (
    <>
      {!isThankYouMobile && <Header />}
      {children}
      {!isThankYouMobile && <Footer />}
    </>
  );
};

/**
 * Jewellery tag short link. The QR printed on a tag encodes /t/<ref> rather
 * than the full /factory/orders path — 10 characters shorter, which is the
 * difference between a 29-module QR and a 33-module one on a tag flap barely
 * 19mm wide. Auth is handled by the factory route this lands on.
 */
const TagRedirect = () => {
  const { ref } = useParams();
  return <Navigate to={`/factory/tag/${encodeURIComponent(ref)}`} replace />;
};

/**
 * The brand splash is a STOREFRONT thing.
 *
 * /admin and /factory are tools someone opens dozens of times a day, and a
 * 2.5s curtain with a tagline on it is 2.5s of nothing in front of work. The
 * storefront keeps it — that is where a first impression is worth paying for.
 *
 * Read off window.location rather than the router: this is decided above
 * <Router>, so there is no router context to ask yet.
 */
const SPLASH_FREE = /^\/(admin|factory)(\/|$)/;
const splashSuppressed = () =>
  typeof window !== "undefined" && SPLASH_FREE.test(window.location.pathname);

const RouteSpinner = () => (
  <div style={{
    minHeight: "60vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#999",
    fontFamily: "Poppins, sans-serif",
    fontSize: 12,
    letterSpacing: 1
  }}>Loading…</div>
);

const UserRouting = () => {
  useEffect(() => {
    Aos.init();
  }, []);


  // Suppressed counts as "already shown" so the timer below never starts, but
  // the session flag is deliberately NOT written: landing on /admin must not
  // spend the storefront's one splash for the session.
  const alreadyShown = sessionStorage.getItem("tdc_splash_shown") || splashSuppressed();
  const [initialLoading, setInitialLoading] = useState(!alreadyShown);
  const [showLoader, setShowLoader] = useState(!alreadyShown);

  useEffect(() => {
    Aos.init();

    if (!alreadyShown) {
      // FIRST TIME APP LOAD LOADER — only on first visit per session
      const timer = setTimeout(() => {
        setInitialLoading(false);
        sessionStorage.setItem("tdc_splash_shown", "1");
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line

  return (
    <>
    {showLoader && (
      <LoadingScreen
        isAppLoading={initialLoading}
        onFinish={() => setShowLoader(false)}
      />
    )}
    <AuthGate>
    <Router>
      <SeoMetaManager />
      <Layout>
        <Suspense fallback={<RouteSpinner />}>
          <Routes>
            <Route path={routes.loginUrl} element={<User />} />
            <Route path={routes.forgotPasswordUrl} element={<ForgotPassword />} />
            <Route path={routes.resetPasswordUrl} element={<ResetPassword />} />
            <Route path={routes.likeUrl} element={<Like />} />
            <Route path={routes.signupUrl} element={<Signup />} />
            <Route path={routes.homepageUrl} element={<Home />} />
            <Route path={routes.orderUrl} element={<OrderPage />} />
            <Route path="/product-detail/:name" element={<AddCart />} />
            <Route path={routes.addressUrl} element={<Address />} />
            <Route path={routes.summeryUrl} element={<Summery />} />
            <Route path={routes.shoppingUrl} element={<Shopping />} />
            <Route path={routes.thankyouUrl} element={<ThankYou />} />
            <Route path={routes.thankyouMobileUrl} element={<ThankYouMobile />} />
            <Route path={routes.pendingPayment} element={<PaymentPending />} />
            <Route path={routes.paymentError} element={<PaymentError />} />
            <Route path={routes.error} element={<Error />} />
            <Route path="/product/*" element={<Catagory />} />
            <Route path={routes.settingUrl} element={<SettingPage />} />
            <Route path={routes.orderviewUrl} element={<OrderView />} />
            <Route path={routes.search} element={<SearchData />} />
            <Route path={routes.faqUrl} element={<Faq />} />

            <Route path={routes.aboutUrl} element={<About />} />
            <Route path={routes.craftsmanshipUrl} element={<Craftsmanship />} />
            <Route path={routes.refundUrl} element={<Refund />} />
            <Route path={routes.privacyUrl} element={<Privacy />} />
            <Route path={routes.contactUrl} element={<Contact />} />
            <Route path={routes.retailerQueryUrl} element={<RetailerQuery />} />
            {/* /sellGold redirects to the working quote tool — the older
                mock info page has been removed. */}
            <Route path={routes.sellGoldtUrl} element={<Navigate to={routes.sellGoldQuoteUrl} replace />} />
            <Route path={routes.sellGoldQuoteUrl} element={<GetAQuote />} />
            <Route path={routes.designCadUrl} element={<DesignCAD />} />
            <Route path={routes.accountUrl} element={<AccountPortal />} />

            {/* Tennis Necklace concept studio */}
            <Route path="/tennis-necklaces" element={<TennisNecklaces />} />
            <Route path="/tennis-necklaces/demo-1" element={<Demo1Drape />} />
            <Route path="/tennis-necklaces/demo-2" element={<Demo2Atelier />} />
            <Route path="/tennis-necklaces/demo-3" element={<Demo3LightTable />} />
            <Route path="/tennis-necklaces/demo-4" element={<Demo4Configurator />} />
            <Route path="/tennis-necklaces/demo-5" element={<Demo5Genome />} />
            <Route path="/tennis-necklaces/demo-6" element={<Demo6Builder />} />
            <Route path="/tennis-necklaces/demo-7" element={<Demo7Wizard />} />

            {/* Lazy-loaded admin module — single chunk */}
            <Route path="/admin/*" element={<AdminRouter />} />

            {/* Lazy-loaded factory module — single chunk */}
            <Route path="/factory/*" element={<FactoryRouter />} />

            {/* Jewellery tag QR target */}
            <Route path="/t/:ref" element={<TagRedirect />} />
          </Routes>
        </Suspense>
      </Layout>
    </Router>
    </AuthGate>
    </>
  );
};

export default UserRouting;
